import { type Command, type JSONContent, Mark, type MarkdownRendererHelpers } from "@tiptap/core";
import type { Node as ProseNode } from "@tiptap/pm/model";
import type { EditorState, Selection, Transaction } from "@tiptap/pm/state";
import { addColumnAfter, addColumnBefore, addRowAfter, addRowBefore, CellSelection, mergeCells, selectedRect, splitCell, TableMap } from "@tiptap/pm/tables";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import { cellAttributes, type Kind, type Pair, tableHtml, validAt, validCaption, validIal, validPairs, validSection, validSpan } from "./table_model";

// The span of a table cell or caption, which draws as the reader's `table caption span` and is nothing else.
export const Span = Mark.create({
	name: "span",
	parseHTML: () => [{ tag: "span[data-cell-span]" }],
	renderHTML: () => ["span", { "data-cell-span": "" }, 0],
});

// What the clipboard carries beside the HTML attributes, so a table cut and pasted keeps its attribute order,
// its caption, its sections and its IAL, as `data-*` the parse rules read back. The clipboard is not trusted: every
// value is checked (table_model.ts) and what does not pass is dropped, so the table pastes plain.
const json =
	(name: string) =>
	(el: HTMLElement): unknown => {
		try {
			return JSON.parse(el.getAttribute(name) ?? "");
		} catch {
			return null;
		}
	};
// Whatever made the node, only allowed attributes come out of it.
const attributes = (pairs: unknown, kind: Kind) => Object.fromEntries(validPairs(pairs, kind) ?? []);
const stored = (pairs: unknown, kind: Kind) => ({ "data-html": JSON.stringify(validPairs(pairs, kind) ?? []) });
const html = (kind: Kind) => ({ default: [] as Pair[], rendered: false, parseHTML: (el: HTMLElement) => validPairs(json("data-html")(el), kind) ?? [] });
const spanOf = (name: string) => (el: HTMLElement) => validSpan(Number(el.getAttribute(name)));

const cell = (base: typeof TableCell, tag: "td" | "th") =>
	base.extend({
		content: "(text | hardBreak | rawHtmlInline)*",
		marks: "link bold italic span",
		addAttributes: () => ({
			colspan: { default: 1, rendered: false, parseHTML: spanOf("colspan") },
			rowspan: { default: 1, rendered: false, parseHTML: spanOf("rowspan") },
			colwidth: { default: null, rendered: false },
			html: html("cell"),
			at: { default: -1, rendered: false, parseHTML: (el: HTMLElement) => validAt(Number(el.getAttribute("data-at") ?? -1)) },
		}),
		parseHTML: () => [{ tag }],
		renderHTML: ({ node }) => {
			const { colspan, rowspan, html: pairs, at } = node.attrs;
			const own = validPairs(pairs, "cell") ?? [];
			return [
				tag,
				{
					...Object.fromEntries(cellAttributes(own, validSpan(colspan), validSpan(rowspan), validAt(at))),
					...stored(own, "cell"),
					"data-at": String(validAt(at)),
				},
				0,
			];
		},
		// A cell holds one paragraph's worth of inline content; Enter breaks the line, as in the book's cells.
		addKeyboardShortcuts() {
			const breakLine = () => this.editor.state.selection.$from.parent.type === this.type && this.editor.commands.insertContent({ type: "hardBreak" });
			return { Enter: breakLine, "Shift-Enter": breakLine };
		},
	});

export const BookCell = cell(TableCell, "td");
export const BookHeader = cell(TableHeader, "th");

export const BookRow = TableRow.extend({
	addAttributes: () => ({
		html: html("tr"),
		section: { default: "tbody", rendered: false, parseHTML: (el: HTMLElement) => validSection(el.getAttribute("data-section")) },
	}),
	renderHTML: ({ node }) => [
		"tr",
		{ ...attributes(node.attrs.html, "tr"), ...stored(node.attrs.html, "tr"), "data-section": validSection(node.attrs.section) },
		0,
	],
});

type Add = (state: EditorState, dispatch?: (tr: Transaction) => void) => boolean;
type Where = { row: number } | { column: number };

// The table around the selection and where it starts.
export function tableAround(selection: Selection): { pos: number; node: ProseNode } | null {
	const { $head } = selection;
	for (let depth = $head.depth; depth > 0; depth -= 1) {
		if ($head.node(depth).type.spec.tableRole === "table") return { pos: $head.before(depth), node: $head.node(depth) };
	}
	return null;
}

// A row or column the table commands add has no attributes; it takes the ones of its neighbour, the row above
// (or the one below when that one is a header row and this is not) and the column before.
function inherit(add: Add, where: (state: EditorState) => Where, side: -1 | 1): Command {
	return ({ state, dispatch }) => {
		if (!dispatch) return add(state);
		const at = tableAround(state.selection)?.pos ?? -1;
		const target = where(state);
		let tr: Transaction | undefined;
		if (!add(state, (made) => (tr = made)) || !tr) return false;
		const table = tr.doc.nodeAt(at) as ProseNode;
		const map = TableMap.get(table);
		const start = at + 1;
		const patch = (cellPos: number, from: number) => {
			const node = table.nodeAt(cellPos) as ProseNode;
			const source = table.nodeAt(from);
			if (source) tr?.setNodeMarkup(start + cellPos, undefined, { ...node.attrs, html: source.attrs.html, at: source.attrs.at });
		};
		if ("row" in target) {
			const { row } = target;
			const kind = (index: number) => table.maybeChild(index)?.firstChild?.type;
			const ref = kind(row + side) && kind(row + side) === kind(row) ? row + side : kind(row - side) ? row - side : row + side;
			let offset = 0;
			for (let i = 0; i < row; i += 1) offset += table.child(i).nodeSize;
			const source = table.maybeChild(ref);
			if (source) tr.setNodeMarkup(start + offset, undefined, { ...table.child(row).attrs, html: source.attrs.html, section: source.attrs.section });
			for (let col = 0; col < map.width; col += 1) {
				const pos = map.map[row * map.width + col];
				if (map.findCell(pos).top === row) patch(pos, map.map[ref * map.width + col]);
			}
		} else {
			for (let row = 0; row < map.height; row += 1) {
				const pos = map.map[row * map.width + target.column];
				if (map.findCell(pos).left === target.column) patch(pos, map.map[row * map.width + target.column + side]);
			}
		}
		dispatch(tr);
		return true;
	};
}

export const BookTable = Table.extend({
	// Markdown pipe tables stay locked blocks: marked reads them, and nothing here parses them.
	markdownTokenizer: { name: "pipeTable", level: "block", start: () => -1, tokenize: () => undefined },
	parseMarkdown: () => null,
	addOptions() {
		return { ...this.parent?.(), View: null as never };
	},
	addAttributes: () => ({
		html: html("table"),
		caption: { default: null, rendered: false, parseHTML: (el: HTMLElement) => validCaption(json("data-caption")(el)) },
		ial: { default: null, rendered: false, parseHTML: (el: HTMLElement) => validIal(el.getAttribute("data-ial")) },
	}),
	renderHTML: ({ node }) => {
		const caption = validCaption(node.attrs.caption);
		const ial = validIal(node.attrs.ial);
		return [
			"table",
			{
				...attributes(node.attrs.html, "table"),
				...stored(node.attrs.html, "table"),
				...(caption ? { "data-caption": JSON.stringify(caption) } : {}),
				...(ial ? { "data-ial": ial } : {}),
			},
			["tbody", 0],
		];
	},
	renderMarkdown: (node: JSONContent, _helpers: MarkdownRendererHelpers) => {
		const body = tableHtml(node) ?? "";
		return node.attrs?.ial ? `${body}\n${node.attrs.ial}` : body;
	},
	addCommands() {
		const commands = this.parent?.() ?? {};
		// The stock merge ignores sections, and a span across the header and the body is not HTML.
		const merge: Command = (props) => {
			const { selection, doc } = props.state;
			if (selection instanceof CellSelection) {
				const sections = new Set<string>();
				selection.forEachCell((_, pos) => sections.add(doc.resolve(pos).parent.attrs.section));
				if (sections.size > 1) return false;
			}
			return mergeCells(props.state, props.dispatch);
		};
		return {
			...commands,
			mergeCells: () => merge,
			mergeOrSplit: () => (props) => merge(props) || splitCell(props.state, props.dispatch),
			addRowAfter: () => inherit(addRowAfter, (state) => ({ row: selectedRect(state).bottom }), -1),
			addRowBefore: () => inherit(addRowBefore, (state) => ({ row: selectedRect(state).top }), 1),
			addColumnAfter: () => inherit(addColumnAfter, (state) => ({ column: selectedRect(state).right }), -1),
			addColumnBefore: () => inherit(addColumnBefore, (state) => ({ column: selectedRect(state).left }), 1),
		};
	},
});

export const tableExtensions = [BookTable, BookRow, BookHeader, BookCell, Span];
